import { z } from 'zod';
/* ------------------------------------------------------------------ primitives */
export const zSnowflake = z.string().regex(/^\d{1,20}$/, 'invalid id');
export const zPermissions = z.string().regex(/^\d{1,20}$/, 'invalid permissions');
export const zIsoDate = z.string();
export const zUsername = z
    .string()
    .min(2)
    .max(32)
    .regex(/^[a-z0-9_.]+$/, 'lowercase letters, digits, "_" and "." only');
export const zPassword = z.string().min(10).max(256);
export const zDisplayName = z.string().trim().min(1).max(32);
export const ChannelType = {
    GUILD_TEXT: 0,
    DM: 1,
    GUILD_VOICE: 2,
    GROUP_DM: 3,
    GUILD_CATEGORY: 4,
    PUBLIC_THREAD: 11,
    PRIVATE_THREAD: 12,
    GUILD_FORUM: 15,
};
export const zChannelType = z.union([
    z.literal(0),
    z.literal(1),
    z.literal(2),
    z.literal(3),
    z.literal(4),
    z.literal(11),
    z.literal(12),
    z.literal(15),
]);
export const isThreadType = (t) => t === ChannelType.PUBLIC_THREAD || t === ChannelType.PRIVATE_THREAD;
export const isGuildChannelType = (t) => t === ChannelType.GUILD_TEXT ||
    t === ChannelType.GUILD_VOICE ||
    t === ChannelType.GUILD_CATEGORY ||
    t === ChannelType.GUILD_FORUM ||
    isThreadType(t);
/** Channels that hold a regular (server-trusted) message history. */
export const isGuildTextLike = (t) => t === ChannelType.GUILD_TEXT || isThreadType(t);
export const isDmChannelType = (t) => t === ChannelType.DM || t === ChannelType.GROUP_DM;
export const zPresenceStatus = z.enum(['online', 'idle', 'dnd', 'invisible', 'offline']);
/* ---------------------------------------------------------------------- users */
export const zUser = z.object({
    id: zSnowflake,
    username: zUsername,
    displayName: z.string(),
    avatarUrl: z.string().nullable(),
    bio: z.string(),
    bot: z.boolean(),
});
export const zSelfUser = zUser.extend({
    isAdmin: z.boolean(),
    mfaEnabled: z.boolean(),
    createdAt: zIsoDate,
});
export const zPresence = z.object({
    userId: zSnowflake,
    status: zPresenceStatus,
    customStatus: z.string().nullable(),
});
export const RelationshipType = { FRIEND: 1, BLOCKED: 2, INCOMING: 3, OUTGOING: 4 };
export const zRelationship = z.object({
    id: zSnowflake, // the other user's id
    type: z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4)]),
    user: zUser,
    since: zIsoDate,
});
/* -------------------------------------------------------------- guild objects */
export const zRole = z.object({
    id: zSnowflake,
    guildId: zSnowflake,
    name: z.string(),
    color: z.number().int(),
    position: z.number().int(),
    permissions: zPermissions,
    hoist: z.boolean(),
    mentionable: z.boolean(),
});
export const zOverwrite = z.object({
    id: zSnowflake,
    type: z.union([z.literal(0), z.literal(1)]),
    allow: zPermissions,
    deny: zPermissions,
});
export const zForumTag = z.object({
    id: zSnowflake,
    name: z.string().trim().min(1).max(20),
    emoji: z.string().max(64).nullable(),
    /** only members with MANAGE_THREADS can apply it */
    moderated: z.boolean(),
});
export const THREAD_AUTO_ARCHIVE_MINUTES = [60, 1440, 4320, 10080];
export const zAutoArchiveMinutes = z.union([
    z.literal(60),
    z.literal(1440),
    z.literal(4320),
    z.literal(10080),
]);
export const zThreadMetadata = z.object({
    archived: z.boolean(),
    /** locked threads only accept messages from members with MANAGE_THREADS */
    locked: z.boolean(),
    autoArchiveMinutes: zAutoArchiveMinutes,
    archivedAt: zIsoDate.nullable(),
    lastActivityAt: zIsoDate,
    messageCount: z.number().int(),
    /** message in the parent channel the thread was started from */
    starterMessageId: zSnowflake.nullable(),
    createdAt: zIsoDate,
});
export const zChannel = z.object({
    id: zSnowflake,
    type: zChannelType,
    guildId: zSnowflake.nullable(),
    name: z.string().nullable(),
    topic: z.string().nullable(),
    position: z.number().int(),
    parentId: zSnowflake.nullable(),
    nsfw: z.boolean(),
    rateLimitPerUser: z.number().int(),
    /** voice channels: max connected members, 0 = unlimited */
    userLimit: z.number().int(),
    permissionOverwrites: z.array(zOverwrite),
    lastMessageId: zSnowflake.nullable(),
    /** DM / group DM only */
    recipients: z.array(zUser).optional(),
    ownerId: zSnowflake.nullable(),
    /** Messages in this channel are end-to-end encrypted (DMs). */
    e2ee: z.boolean(),
    /** threads only (ownerId is the thread's creator) */
    threadMetadata: zThreadMetadata.optional(),
    /** threads in a forum: ids of the forum's tags applied to this post */
    appliedTags: z.array(zSnowflake).optional(),
    /** forum channels only */
    availableTags: z.array(zForumTag).optional(),
});
/** Custom emoji: used as `<:name:id>` (or `<a:name:id>` when animated) in messages. */
export const zGuildEmoji = z.object({
    id: zSnowflake,
    guildId: zSnowflake,
    name: z.string(),
    animated: z.boolean(),
    url: z.string(),
    creatorId: zSnowflake.nullable(),
});
export const zSticker = z.object({
    id: zSnowflake,
    guildId: zSnowflake,
    name: z.string(),
    description: z.string(),
    url: z.string(),
});
export const MAX_GUILD_EMOJIS = 100;
export const MAX_GUILD_STICKERS = 30;
export const zEmojiName = z
    .string()
    .regex(/^[A-Za-z0-9_]{2,32}$/, '2-32 letters, digits or underscores');
/** `<:name:id>` / `<a:name:id>` in message content */
export const CUSTOM_EMOJI_RE = /<(a?):([A-Za-z0-9_]{2,32}):(\d{1,20})>/g;
/** custom emoji reactions are stored as `name:id` */
export const CUSTOM_REACTION_RE = /^([A-Za-z0-9_]{2,32}):(\d{1,20})$/;
export const customEmojiUrl = (id) => `/api/v1/emojis/${id}`;
export const stickerUrl = (id) => `/api/v1/stickers/${id}`;
/* --------------------------------------------------------------------- events */
export const EventStatus = { SCHEDULED: 1, ACTIVE: 2, COMPLETED: 3, CANCELED: 4 };
export const zEventStatus = z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4)]);
export const zGuildEvent = z.object({
    id: zSnowflake,
    guildId: zSnowflake,
    /** voice-channel events; null for events somewhere else */
    channelId: zSnowflake.nullable(),
    /** where an external event happens */
    location: z.string().nullable(),
    name: z.string(),
    description: z.string(),
    startAt: zIsoDate,
    endAt: zIsoDate.nullable(),
    status: zEventStatus,
    creatorId: zSnowflake.nullable(),
    interestedCount: z.number().int(),
});
const zEventFields = z.object({
    name: z.string().trim().min(1).max(100),
    description: z.string().max(1000).default(''),
    startAt: z.iso.datetime({ offset: true }),
    endAt: z.iso.datetime({ offset: true }).nullable().optional(),
    channelId: zSnowflake.nullable().optional(),
    location: z.string().trim().min(1).max(100).nullable().optional(),
});
export const zCreateEventBody = zEventFields.refine((b) => !!b.channelId !== !!b.location, {
    message: 'Pick a voice channel or a location',
});
export const zUpdateEventBody = zEventFields.partial().extend({
    // no default here: an edit that doesn't mention the description must keep it
    description: z.string().max(1000).optional(),
    /** start (2), end (3) or cancel (4) */
    status: z.union([z.literal(2), z.literal(3), z.literal(4)]).optional(),
});
export const zMember = z.object({
    guildId: zSnowflake,
    user: zUser,
    nickname: z.string().nullable(),
    roles: z.array(zSnowflake),
    joinedAt: zIsoDate,
    communicationDisabledUntil: zIsoDate.nullable(),
});
export const zGuild = z.object({
    id: zSnowflake,
    name: z.string(),
    description: z.string().nullable(),
    iconUrl: z.string().nullable(),
    ownerId: zSnowflake,
    systemChannelId: zSnowflake.nullable(),
    createdAt: zIsoDate,
});
/** Full guild payload sent on READY / GUILD_CREATE. */
export const zGuildFull = zGuild.extend({
    roles: z.array(zRole),
    channels: z.array(zChannel),
    members: z.array(zMember),
    memberCount: z.number().int(),
    /** threads (visible in `channels`) the viewer has joined */
    joinedThreadIds: z.array(zSnowflake),
    emojis: z.array(zGuildEmoji),
    stickers: z.array(zSticker),
    /** upcoming and live events the viewer can see */
    events: z.array(zGuildEvent),
    /** events the viewer marked as interested */
    interestedEventIds: z.array(zSnowflake),
});
export const zInvite = z.object({
    code: z.string(),
    guild: zGuild.pick({ id: true, name: true, iconUrl: true, description: true }),
    channelId: zSnowflake.nullable(),
    inviterId: zSnowflake.nullable(),
    uses: z.number().int(),
    maxUses: z.number().int(),
    expiresAt: zIsoDate.nullable(),
    createdAt: zIsoDate,
    memberCount: z.number().int().optional(),
});
export const zBan = z.object({ user: zUser, reason: z.string().nullable() });
/* ------------------------------------------------------------------- messages */
export const MessageType = {
    DEFAULT: 0,
    /** posted in an AutoMod alert channel; content is the offending message */
    AUTOMOD_ACTION: 24,
    MEMBER_JOIN: 7,
    PIN_NOTICE: 6,
    THREAD_CREATED: 18,
    REPLY: 19,
    THREAD_STARTER: 21,
    /** a DM switched between end-to-end encrypted and standard; content is 'e2ee' or 'standard' */
    DM_MODE_CHANGE: 40,
};
export const zAttachment = z.object({
    id: zSnowflake,
    filename: z.string(),
    contentType: z.string(),
    size: z.number().int(),
    url: z.string(),
    width: z.number().int().nullable(),
    height: z.number().int().nullable(),
});
/** A link preview, or a GIF picked from search. */
export const zLinkEmbed = z.object({
    url: z.string(),
    title: z.string().nullable(),
    description: z.string().nullable(),
    siteName: z.string().nullable(),
    imageUrl: z.string().nullable(),
    /** 'gif' for GIFs picked from search; absent for link previews */
    type: z.literal('gif').optional(),
    width: z.number().int().optional(),
    height: z.number().int().optional(),
});
/* Rich embeds: cards that bots and webhooks post. */
export const MAX_EMBEDS = 10;
/** characters across every embed's text in one message */
export const MAX_EMBED_CHARS = 6000;
const zHttpUrl = z
    .string()
    .max(2048)
    .refine((u) => {
    try {
        return ['http:', 'https:'].includes(new URL(u).protocol);
    }
    catch {
        return false;
    }
}, 'must be an http(s) URL');
/** An image in a stored embed: re-hosted by this server, never a third-party URL. */
const zEmbedImage = z.object({
    url: z.string(),
    width: z.number().int(),
    height: z.number().int(),
});
const zEmbedField = z.object({
    name: z.string().trim().min(1).max(256),
    value: z.string().trim().min(1).max(1024),
    inline: z.boolean().default(false),
});
export const zRichEmbed = z.object({
    type: z.literal('rich'),
    title: z.string().nullable(),
    url: z.string().nullable(),
    description: z.string().nullable(),
    /** 0xRRGGBB */
    color: z.number().int().nullable(),
    timestamp: zIsoDate.nullable(),
    author: z
        .object({ name: z.string(), url: z.string().nullable(), iconUrl: z.string().nullable() })
        .nullable(),
    fields: z.array(zEmbedField),
    image: zEmbedImage.nullable(),
    thumbnail: zEmbedImage.nullable(),
    footer: z.object({ text: z.string(), iconUrl: z.string().nullable() }).nullable(),
});
/** What bots and webhooks send. Images are fetched and re-hosted by the server. */
export const zRichEmbedInput = z
    .object({
    title: z.string().trim().min(1).max(256).optional(),
    url: zHttpUrl.optional(),
    description: z.string().trim().min(1).max(4096).optional(),
    color: z.number().int().min(0).max(0xffffff).optional(),
    timestamp: z.iso.datetime({ offset: true }).optional(),
    author: z
        .object({
        name: z.string().trim().min(1).max(256),
        url: zHttpUrl.optional(),
        iconUrl: zHttpUrl.optional(),
    })
        .optional(),
    fields: z.array(zEmbedField).max(25).default([]),
    image: z.object({ url: zHttpUrl }).optional(),
    thumbnail: z.object({ url: zHttpUrl }).optional(),
    footer: z
        .object({ text: z.string().trim().min(1).max(2048), iconUrl: zHttpUrl.optional() })
        .optional(),
})
    .refine((e) => !!(e.title || e.description || e.fields.length || e.image || e.author || e.footer), 'an embed needs a title, description, fields, an image, an author or a footer');
export const embedText = (e) => [
    e.title,
    e.description,
    e.author?.name,
    ...e.fields.flatMap((f) => [f.name, f.value]),
    e.footer?.text,
]
    .filter(Boolean)
    .join('\n');
export const zRichEmbedsInput = z
    .array(zRichEmbedInput)
    .max(MAX_EMBEDS)
    .refine((es) => es.reduce((n, e) => n + embedText(e).length, 0) <= MAX_EMBED_CHARS, `embeds may have at most ${MAX_EMBED_CHARS} characters of text in total`);
export const zEmbed = z.union([zRichEmbed, zLinkEmbed]);
/* Message components: rows of buttons, or a select menu, on a bot's message. */
export const MAX_COMPONENT_ROWS = 5;
/** an emoji on a button or select option: a Unicode emoji, or `name:id` for a custom one */
const zComponentEmoji = z
    .string()
    .max(64)
    .refine((e) => CUSTOM_REACTION_RE.test(e) ||
    (/^(\p{Extended_Pictographic}|\p{Regional_Indicator}|[#*0-9]️?⃣)/u.test(e) && !/\s/.test(e)), 'a Unicode emoji or name:id');
const zCustomId = z.string().min(1).max(100);
export const zButtonStyle = z.enum(['primary', 'secondary', 'success', 'danger', 'link']);
export const zButton = z
    .object({
    type: z.literal('button'),
    style: zButtonStyle,
    label: z.string().trim().min(1).max(80).optional(),
    emoji: zComponentEmoji.optional(),
    /** sent back to the bot when clicked; every style except link */
    customId: zCustomId.optional(),
    /** link buttons only: opens this page */
    url: zHttpUrl.optional(),
    disabled: z.boolean().default(false),
})
    .refine((b) => !!(b.label || b.emoji), 'a button needs a label or an emoji')
    .refine((b) => (b.style === 'link' ? !!b.url && !b.customId : !!b.customId && !b.url), 'link buttons need a url, other buttons a customId');
export const zSelectOption = z.object({
    label: z.string().trim().min(1).max(100),
    value: z.string().min(1).max(100),
    description: z.string().trim().max(100).optional(),
    emoji: zComponentEmoji.optional(),
    /** selected when the menu is first shown */
    default: z.boolean().default(false),
});
export const zSelect = z
    .object({
    type: z.literal('select'),
    customId: zCustomId,
    placeholder: z.string().trim().max(150).optional(),
    minValues: z.number().int().min(0).max(25).default(1),
    maxValues: z.number().int().min(1).max(25).default(1),
    options: z.array(zSelectOption).min(1).max(25),
    disabled: z.boolean().default(false),
})
    .refine((s) => s.minValues <= s.maxValues && s.maxValues <= s.options.length, 'needs minValues <= maxValues <= the number of options')
    .refine((s) => new Set(s.options.map((o) => o.value)).size === s.options.length, 'option values must differ');
export const zActionRow = z
    .object({
    type: z.literal('row'),
    components: z
        .array(z.union([zButton, zSelect]))
        .min(1)
        .max(5),
})
    .refine((r) => r.components.every((c) => c.type === 'button') || r.components.length === 1, 'a row holds up to 5 buttons or a single select menu');
export const zComponents = z
    .array(zActionRow)
    .max(MAX_COMPONENT_ROWS)
    .refine((rows) => {
    const ids = rows.flatMap((r) => r.components.flatMap((c) => (c.customId ? [c.customId] : [])));
    return new Set(ids).size === ids.length;
}, 'customId values must differ within a message');
export const findComponent = (rows, customId) => rows.flatMap((r) => r.components).find((c) => c.customId === customId) ?? null;
/** A GIF search result. All URLs point at this server, which proxies Giphy. */
export const zGif = z.object({
    id: z.string(),
    title: z.string(),
    width: z.number().int(),
    height: z.number().int(),
    previewUrl: z.string(),
    url: z.string(),
});
export const zGifSearchQuery = z.object({
    q: z.string().max(50).default(''),
    offset: z.coerce.number().int().min(0).max(4999).default(0),
});
export const isRichEmbed = (e) => e.type === 'rich';
export const zReaction = z.object({
    emoji: z.string(),
    count: z.number().int(),
    me: z.boolean(),
});
export const zPoll = z.object({
    question: z.string(),
    answers: z.array(z.object({
        id: z.number().int(),
        text: z.string(),
        count: z.number().int(),
        /** whether the viewer voted for it */
        me: z.boolean(),
    })),
    allowMultiselect: z.boolean(),
    expiresAt: zIsoDate,
    /** closed early by its author/a moderator, or past expiresAt */
    closed: z.boolean(),
    /** number of people who voted */
    totalVoters: z.number().int(),
});
export const zCreatePoll = z.object({
    question: z.string().trim().min(1).max(300),
    answers: z.array(z.string().trim().min(1).max(55)).min(1).max(10),
    allowMultiselect: z.boolean().default(false),
    /** 1 hour to 32 days */
    durationHours: z.number().int().min(1).max(768).default(24),
});
export const zPollVoteBody = z.object({
    /** empty to remove your vote */
    answerIds: z.array(z.number().int().min(0).max(9)).max(10),
});
const zMessageBase = z.object({
    id: zSnowflake,
    channelId: zSnowflake,
    guildId: zSnowflake.nullable(),
    author: zUser,
    type: z.number().int(),
    content: z.string(),
    createdAt: zIsoDate,
    editedAt: zIsoDate.nullable(),
    attachments: z.array(zAttachment),
    embeds: z.array(zEmbed),
    mentions: z.array(zSnowflake),
    mentionRoles: z.array(zSnowflake),
    mentionEveryone: z.boolean(),
    reactions: z.array(zReaction),
    pinned: z.boolean(),
    referenceId: zSnowflake.nullable(),
    stickers: z.array(zSticker.pick({ id: true, name: true, url: true })),
    /** posted through this incoming webhook (author is the webhook's bot user) */
    webhookId: zSnowflake.nullable().optional(),
    /** the reply to a slash command: who ran which command */
    interaction: z
        .object({
        id: zSnowflake,
        /** the command's name; empty for a click on a button or select menu */
        name: z.string(),
        type: z.enum(['command', 'component']).optional(),
        user: zUser,
    })
        .nullable()
        .optional(),
    /** buttons and select menus (bots' messages) */
    components: z.array(zActionRow).optional(),
    poll: zPoll.nullable().optional(),
    /** set on a message that a thread was started from */
    thread: z
        .object({
        id: zSnowflake,
        name: z.string(),
        messageCount: z.number().int(),
        lastActivityAt: zIsoDate,
        archived: z.boolean(),
    })
        .nullable()
        .optional(),
});
export const zMessage = zMessageBase.extend({
    referencedMessage: z.lazy(() => zMessage.nullable()).optional(),
});
export const zReadState = z.object({
    channelId: zSnowflake,
    lastReadMessageId: zSnowflake.nullable(),
    mentionCount: z.number().int(),
});
export const zNotificationLevel = z.enum(['all', 'mentions', 'nothing']);
export const zNotificationSetting = z.object({
    /** guild id or channel id */
    targetId: zSnowflake,
    muted: z.boolean(),
    level: zNotificationLevel.nullable(),
});
/* ---------------------------------------------------------------------- E2EE */
export const zDevice = z.object({
    id: zSnowflake,
    userId: zSnowflake,
    name: z.string(),
    /** base64 MLS signature public key — used for safety-number verification */
    identityKey: z.string(),
    createdAt: zIsoDate,
    lastSeenAt: zIsoDate.nullable(),
    revoked: z.boolean(),
});
export const zClaimedKeyPackage = z.object({
    userId: zSnowflake,
    deviceId: zSnowflake,
    keyPackage: z.string(),
});
export const zE2eeMessageKind = z.enum(['application', 'commit']);
/** An opaque MLS message relayed by the server. The server never sees plaintext. */
export const zE2eeMessage = z.object({
    id: zSnowflake,
    channelId: zSnowflake,
    senderUserId: zSnowflake,
    senderDeviceId: zSnowflake,
    kind: zE2eeMessageKind,
    generation: z.number().int(),
    epoch: z.number().int(),
    ciphertext: z.string(),
    createdAt: zIsoDate,
});
export const zMlsWelcome = z.object({
    id: zSnowflake,
    channelId: zSnowflake,
    senderDeviceId: zSnowflake,
    welcome: z.string(),
    generation: z.number().int(),
    /** id of the commit message that this welcome belongs to */
    commitId: zSnowflake.nullable(),
    createdAt: zIsoDate,
});
export const zE2eeState = z.object({
    channelId: zSnowflake,
    generation: z.number().int(),
    epoch: z.number().int(),
    groupDeviceIds: z.array(zSnowflake),
    recipientIds: z.array(zSnowflake),
    /** active devices of all recipients */
    devices: z.array(zDevice),
    /** epoch of the published GroupInfo (lets new devices join on their own); null if none */
    groupInfoEpoch: z.number().int().nullable(),
});
/* --------------------------------------------------------------------- voice */
export const zVoiceState = z.object({
    guildId: zSnowflake.nullable(),
    /** null when the user just disconnected */
    channelId: zSnowflake.nullable(),
    userId: zSnowflake,
    selfMute: z.boolean(),
    selfDeaf: z.boolean(),
    serverMute: z.boolean(),
    serverDeaf: z.boolean(),
    selfVideo: z.boolean(),
    selfStream: z.boolean(),
    /** whose screen share this user is watching (same channel) */
    watchingUserId: zSnowflake.nullable(),
    joinedAt: zIsoDate,
});
/** An ongoing DM / group-DM call. Media is end-to-end encrypted with a key derived from the DM's MLS group. */
export const zCallState = z.object({
    channelId: zSnowflake,
    startedBy: zSnowflake,
    startedAt: zIsoDate,
    /** connected users */
    participants: z.array(zSnowflake),
    /** users being rung who haven't joined or declined */
    ringing: z.array(zSnowflake),
});
export const zVoiceJoinResponse = z.object({
    channelId: zSnowflake,
    /** LiveKit server URL (wss://…) */
    url: z.string(),
    token: z.string(),
});
export const zUpdateVoiceSelfBody = z.object({
    selfMute: z.boolean().optional(),
    selfDeaf: z.boolean().optional(),
    selfVideo: z.boolean().optional(),
    selfStream: z.boolean().optional(),
    /** start (a user id) or stop (null) watching someone's screen share */
    watchingUserId: zSnowflake.nullable().optional(),
});
/** Highest screen-share quality this server allows. */
export const zStreamQuality = z.enum(['720p30', '1080p30', '1080p60']);
export const zModerateVoiceBody = z.object({
    serverMute: z.boolean().optional(),
    serverDeaf: z.boolean().optional(),
    /** move to another voice channel, or null to disconnect */
    channelId: zSnowflake.nullable().optional(),
});
/* ----------------------------------------------------------- request bodies */
export const zRegisterBody = z.object({
    username: zUsername,
    password: zPassword,
    displayName: zDisplayName.optional(),
    inviteCode: z.string().min(1).max(64),
});
export const zLoginBody = z.object({
    username: z.string().min(1).max(64),
    password: z.string().min(1).max(256),
    totp: z.string().max(32).optional(),
    deviceName: z.string().max(64).optional(),
});
export const zAuthResponse = z.object({
    user: zSelfUser,
    /** Bearer token for non-browser clients. Browsers use the HttpOnly cookie instead. */
    token: z.string(),
    csrfToken: z.string(),
});
export const zMfaRequired = z.object({ mfaRequired: z.literal(true) });
export const zSession = z.object({
    id: zSnowflake,
    name: z.string(),
    ip: z.string().nullable(),
    userAgent: z.string().nullable(),
    createdAt: zIsoDate,
    lastUsedAt: zIsoDate,
    current: z.boolean(),
});
export const zUpdateProfileBody = z.object({
    displayName: zDisplayName.optional(),
    bio: z.string().max(190).optional(),
    avatarAttachmentId: zSnowflake.nullable().optional(),
});
export const zChangePasswordBody = z.object({
    currentPassword: z.string(),
    newPassword: zPassword,
});
export const zCreateGuildBody = z.object({ name: z.string().trim().min(2).max(100) });
export const zUpdateGuildBody = z.object({
    name: z.string().trim().min(2).max(100).optional(),
    description: z.string().max(300).nullable().optional(),
    iconAttachmentId: zSnowflake.nullable().optional(),
    systemChannelId: zSnowflake.nullable().optional(),
});
export const zCreateChannelBody = z.object({
    name: z.string().trim().min(1).max(100),
    type: z.union([z.literal(0), z.literal(2), z.literal(4), z.literal(15)]).default(0),
    topic: z.string().max(1024).nullable().optional(),
    parentId: zSnowflake.nullable().optional(),
    nsfw: z.boolean().optional(),
    rateLimitPerUser: z.number().int().min(0).max(21600).optional(),
    userLimit: z.number().int().min(0).max(99).optional(),
    permissionOverwrites: z.array(zOverwrite).optional(),
    /** forums: post tags (ids are assigned by the server when omitted) */
    availableTags: z
        .array(zForumTag.extend({ id: zSnowflake.optional() }))
        .max(20)
        .optional(),
});
export const zUpdateChannelBody = zCreateChannelBody
    .omit({ type: true })
    .extend({
    /** threads */
    archived: z.boolean(),
    locked: z.boolean(),
    autoArchiveMinutes: zAutoArchiveMinutes,
    appliedTags: z.array(zSnowflake).max(5),
    /** DMs: end-to-end encrypted (true) or standard (false) */
    e2ee: z.boolean(),
})
    .partial();
export const zCreateThreadBody = z.object({
    name: z.string().trim().min(1).max(100),
    /** private threads are only visible to their members (and MANAGE_THREADS) */
    type: z.union([z.literal(11), z.literal(12)]).default(11),
    autoArchiveMinutes: zAutoArchiveMinutes.default(1440),
    /** start the thread from this message in the parent channel */
    messageId: zSnowflake.optional(),
    /** forum posts: the opening message (required in forums) */
    message: z
        .object({
        content: z.string().max(4000).default(''),
        attachmentIds: z.array(zSnowflake).max(10).default([]),
    })
        .refine((b) => b.content.trim().length > 0 || b.attachmentIds.length > 0, {
        message: 'message must have content or attachments',
    })
        .optional(),
    appliedTags: z.array(zSnowflake).max(5).optional(),
});
export const zThreadsQuery = z.object({
    archived: z
        .enum(['true', 'false'])
        .default('false')
        .transform((v) => v === 'true'),
    before: zIsoDate.optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
});
export const zThreadList = z.object({
    threads: z.array(zChannel),
    /** first message of each thread, for forum previews */
    firstMessages: z.record(zSnowflake, zMessage).optional(),
    hasMore: z.boolean(),
});
export const zChannelPositionsBody = z
    .array(z.object({
    id: zSnowflake,
    position: z.number().int(),
    parentId: zSnowflake.nullable().optional(),
}))
    .max(500);
export const zCreateRoleBody = z.object({
    name: z.string().trim().min(1).max(100).default('new role'),
    color: z.number().int().min(0).max(0xffffff).default(0),
    permissions: zPermissions.optional(),
    hoist: z.boolean().default(false),
    mentionable: z.boolean().default(false),
});
// Explicit (not zCreateRoleBody.partial()): the create defaults would otherwise fill in and
// reset fields a partial update didn't mention.
export const zUpdateRoleBody = z.object({
    name: z.string().trim().min(1).max(100).optional(),
    color: z.number().int().min(0).max(0xffffff).optional(),
    permissions: zPermissions.optional(),
    hoist: z.boolean().optional(),
    mentionable: z.boolean().optional(),
});
export const zRolePositionsBody = z
    .array(z.object({ id: zSnowflake, position: z.number().int().min(1) }))
    .max(250);
export const zUpdateMemberBody = z.object({
    nickname: z.string().max(32).nullable().optional(),
    roles: z.array(zSnowflake).max(250).optional(),
    communicationDisabledUntil: zIsoDate.nullable().optional(),
});
export const zBanBody = z.object({
    reason: z.string().max(512).optional(),
    deleteMessageSeconds: z.number().int().min(0).max(604800).optional(),
});
export const zCreateInviteBody = z.object({
    maxAge: z.number().int().min(0).max(604800).default(86400),
    maxUses: z.number().int().min(0).max(100).default(0),
});
export const MAX_MESSAGE_LENGTH = 4000;
export const zCreateMessageBody = z
    .object({
    content: z.string().max(MAX_MESSAGE_LENGTH).default(''),
    attachmentIds: z.array(zSnowflake).max(10).default([]),
    referenceId: zSnowflake.optional(),
    nonce: z.string().max(64).optional(),
    /** stickers from this server */
    stickerIds: z.array(zSnowflake).max(3).default([]),
    poll: zCreatePoll.optional(),
    /** a GIF from GIF search */
    gifId: z
        .string()
        .regex(/^[A-Za-z0-9]{5,40}$/)
        .optional(),
    /** rich embeds (bots only) */
    embeds: zRichEmbedsInput.optional(),
    /** buttons and select menus (bots only) */
    components: zComponents.optional(),
})
    .refine((b) => b.content.trim().length > 0 ||
    b.attachmentIds.length > 0 ||
    b.stickerIds.length > 0 ||
    !!b.poll ||
    !!b.gifId ||
    !!b.embeds?.length, { message: 'message must have content, attachments, a sticker, a poll, a GIF or an embed' });
export const zEditMessageBody = z
    .object({
    content: z.string().max(MAX_MESSAGE_LENGTH).optional(),
    /** replaces the message's rich embeds (bots only) */
    embeds: zRichEmbedsInput.optional(),
    /** replaces the message's buttons and select menus (bots only) */
    components: zComponents.optional(),
})
    .refine((b) => b.content !== undefined || b.embeds !== undefined || b.components !== undefined, {
    message: 'nothing to change',
});
export const zMessagesQuery = z.object({
    before: zSnowflake.optional(),
    after: zSnowflake.optional(),
    around: zSnowflake.optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
});
export const zSearchQuery = z.object({
    q: z.string().min(1).max(200),
    channelId: zSnowflake.optional(),
    authorId: zSnowflake.optional(),
    limit: z.coerce.number().int().min(1).max(50).default(25),
    offset: z.coerce.number().int().min(0).max(5000).default(0),
});
export const zCreateUploadBody = z.object({
    filename: z.string().min(1).max(255),
    contentType: z.string().min(1).max(255),
    size: z.number().int().min(1),
    /** encrypted blobs for E2EE DMs; server will not inspect or transform them */
    encrypted: z.boolean().default(false),
    /** required for encrypted uploads: the DM channel the file is shared in */
    channelId: zSnowflake.optional(),
});
export const zUploadTicket = z.object({
    attachmentId: zSnowflake,
    uploadUrl: z.string(),
    method: z.literal('PUT'),
    headers: z.record(z.string(), z.string()),
});
export const zCreateDmBody = z.object({
    recipientIds: z.array(zSnowflake).min(1).max(9),
    /** start end-to-end encrypted (default: standard); ignored when reopening an existing DM */
    e2ee: z.boolean().optional(),
});
export const zRegisterDeviceBody = z.object({
    name: z.string().min(1).max(64),
    identityKey: z.string().min(1).max(4096),
});
export const zUploadKeyPackagesBody = z.object({
    keyPackages: z.array(z.string().min(1).max(16384)).min(1).max(100),
});
export const zClaimKeyPackagesBody = z.object({
    userIds: z.array(zSnowflake).min(1).max(10),
    /** only claim for these devices (default: all active devices of the users) */
    deviceIds: z.array(zSnowflake).max(100).optional(),
});
const zGroupInfo = z.string().min(1).max(524288);
export const zPostE2eeMessageBody = z.object({
    deviceId: zSnowflake,
    kind: zE2eeMessageKind,
    generation: z.number().int().min(0),
    epoch: z.number().int().min(0),
    ciphertext: z.string().min(1).max(262144),
    /** For commits that add devices: per-recipient welcomes. */
    welcome: z
        .object({
        welcome: z.string().min(1).max(262144),
        deviceIds: z.array(zSnowflake).min(1).max(100),
    })
        .optional(),
    /** For commits: the full device roster after the commit (advisory, used for UI/repair). */
    memberDeviceIds: z.array(zSnowflake).max(100).optional(),
    /** For commits: the group's GroupInfo after the commit, so new devices can join on their own. */
    groupInfo: zGroupInfo.optional(),
});
/** Publish the GroupInfo for the current epoch (groups created before GroupInfo was kept). */
export const zPutGroupInfoBody = z.object({
    deviceId: zSnowflake,
    generation: z.number().int().min(0),
    epoch: z.number().int().min(0),
    groupInfo: zGroupInfo,
});
export const zGroupInfoResponse = z.object({
    generation: z.number().int(),
    epoch: z.number().int(),
    groupInfo: z.string(),
});
/* ----------------------------------------------------------------------- bots */
/** A bot account owned by a person. Its id is the bot user's id. */
export const zApplication = z.object({
    id: zSnowflake,
    name: z.string(),
    avatarUrl: z.string().nullable(),
    /** anyone who can manage a server may add it (otherwise only its owner) */
    public: z.boolean(),
    ownerId: zSnowflake,
    createdAt: zIsoDate,
    user: zUser,
});
export const MAX_BOTS_PER_USER = 10;
export const zCreateApplicationBody = z.object({
    name: z.string().trim().min(2).max(32),
    public: z.boolean().default(false),
});
export const zUpdateApplicationBody = z.object({
    name: z.string().trim().min(2).max(32).optional(),
    public: z.boolean().optional(),
    avatarAttachmentId: zSnowflake.nullable().optional(),
});
/** Returned when a bot is created or its token reset; the token is never shown again. */
export const zApplicationWithToken = z.object({ application: zApplication, token: z.string() });
/* ------------------------------------------------------------ slash commands */
export const ApplicationCommandOptionType = {
    STRING: 3,
    INTEGER: 4,
    BOOLEAN: 5,
    USER: 6,
    CHANNEL: 7,
    ROLE: 8,
};
export const MAX_COMMANDS_PER_SCOPE = 50;
export const MAX_COMMAND_OPTIONS = 10;
export const zCommandName = z
    .string()
    .regex(/^[a-z0-9_-]{1,32}$/, 'lowercase letters, digits, - and _');
export const zCommandOption = z.object({
    type: z.union([
        z.literal(3),
        z.literal(4),
        z.literal(5),
        z.literal(6),
        z.literal(7),
        z.literal(8),
    ]),
    name: zCommandName,
    description: z.string().trim().min(1).max(100),
    required: z.boolean().default(false),
});
export const zCommandBody = z.object({
    name: zCommandName,
    description: z.string().trim().min(1).max(100),
    options: z
        .array(zCommandOption)
        .max(MAX_COMMAND_OPTIONS)
        .default([])
        .refine((o) => new Set(o.map((x) => x.name)).size === o.length, 'option names must differ')
        .refine((o) => o.every((x, i) => !x.required || o.slice(0, i).every((y) => y.required)), 'required options come first'),
});
/** Bulk replace: every command in this scope. */
export const zBulkCommandsBody = z
    .array(zCommandBody)
    .max(MAX_COMMANDS_PER_SCOPE)
    .refine((c) => new Set(c.map((x) => x.name)).size === c.length, 'command names must differ');
export const zApplicationCommand = z.object({
    id: zSnowflake,
    applicationId: zSnowflake,
    /** null: available in every server the bot is in */
    guildId: zSnowflake.nullable(),
    name: z.string(),
    description: z.string(),
    options: z.array(zCommandOption),
});
export const zInteractionOptionValue = z.union([z.string().max(1000), z.number(), z.boolean()]);
export const zCreateInteractionBody = z.object({
    commandId: zSnowflake,
    options: z.record(z.string(), zInteractionOptionValue).default({}),
});
const zInteractionBase = z.object({
    id: zSnowflake,
    /** authenticates the reply: POST /interactions/:id/:token/callback */
    token: z.string(),
    applicationId: zSnowflake,
    guildId: zSnowflake,
    channelId: zSnowflake,
    user: zUser,
});
export const zInteraction = z.discriminatedUnion('type', [
    /** someone ran one of the bot's slash commands */
    zInteractionBase.extend({
        type: z.literal('command'),
        data: z.object({
            id: zSnowflake,
            name: z.string(),
            options: z.array(z.object({ name: z.string(), type: z.number().int(), value: zInteractionOptionValue })),
        }),
    }),
    /** someone clicked a button or picked from a select menu on the bot's message */
    zInteractionBase.extend({
        type: z.literal('component'),
        data: z.object({
            customId: z.string(),
            componentType: z.enum(['button', 'select']),
            /** the picked option values (select menus) */
            values: z.array(z.string()),
        }),
        /** the message the component is on */
        message: z.lazy(() => zMessage),
    }),
]);
/** Clicking a button or choosing from a select menu on a bot's message. */
export const zComponentInteractionBody = z.object({
    customId: zCustomId,
    values: z.array(z.string().max(100)).max(25).default([]),
});
const zReplyContent = {
    content: z.string().trim().max(2000).default(''),
    embeds: zRichEmbedsInput.optional(),
    components: zComponents.optional(),
};
export const zInteractionCallbackBody = z.discriminatedUnion('type', [
    z
        .object({
        type: z.literal('message'),
        ...zReplyContent,
        /** only the person who used the command or component sees it, and it isn't stored */
        ephemeral: z.boolean().default(false),
    })
        .refine((b) => b.content.length > 0 || !!b.embeds?.length, {
        message: 'a reply needs content or embeds',
    }),
    /** "thinking...": reply later (within 15 minutes) with a message */
    z.object({ type: z.literal('defer') }),
    /** components only: edit the message the component is on */
    z.object({
        type: z.literal('update'),
        content: z.string().trim().max(2000).optional(),
        embeds: zRichEmbedsInput.optional(),
        components: zComponents.optional(),
    }),
    /** components only: acknowledge now, then update the message within 15 minutes */
    z.object({ type: z.literal('deferred_update') }),
]);
/* ---------------------------------------------------------------- native push */
/**
 * An app's encryption keys for push payloads (RFC 8291): its P-256 public key
 * (uncompressed point, base64url) and a 16-byte auth secret (base64url).
 */
export const zPushKeys = z.object({
    p256dh: z.string().regex(/^B[A-Za-z0-9_-]{86}$/, 'an uncompressed P-256 public key, base64url'),
    auth: z.string().regex(/^[A-Za-z0-9_-]{22}$/, '16 random bytes, base64url'),
});
export const zNativePushDevice = z.discriminatedUnion('platform', [
    z.object({
        platform: z.literal('apns'),
        /** the device token from registerForRemoteNotifications, hex */
        token: z.string().regex(/^[0-9a-fA-F]{64,200}$/, 'an APNs device token (hex)'),
        keys: zPushKeys,
        /** development builds get sandbox tokens */
        environment: z.enum(['production', 'sandbox']).default('production'),
    }),
    /**
     * iOS PushKit (VoIP) token: used only to ring for incoming calls, so the app can show
     * the system call screen with CallKit. Register it alongside the regular APNs token.
     */
    z.object({
        platform: z.literal('apns-voip'),
        token: z.string().regex(/^[0-9a-fA-F]{64,200}$/, 'a PushKit device token (hex)'),
        keys: zPushKeys,
        environment: z.enum(['production', 'sandbox']).default('production'),
    }),
    z.object({
        platform: z.literal('fcm'),
        /** the registration token from FirebaseMessaging.getToken() */
        token: z.string().regex(/^[\w:-]{32,4096}$/, 'an FCM registration token'),
        keys: zPushKeys,
    }),
]);
export const zNativePushInfo = z.object({
    /** null: this server doesn't deliver through APNs */
    apns: z.object({ bundleId: z.string() }).nullable(),
    fcm: z
        .object({
        projectId: z.string(),
        /** what the Android app needs to receive this server's pushes (public identifiers) */
        android: z.object({ appId: z.string(), apiKey: z.string(), senderId: z.string() }).nullable(),
    })
        .nullable(),
});
/* ---------------------------------------------------------------- android app */
/** The newest Android app this server offers at /download/Comms.apk. */
export const zAndroidRelease = z.discriminatedUnion('available', [
    z.object({ available: z.literal(false) }),
    z.object({
        available: z.literal(true),
        /** compare with the CommsAndroid/<versionCode> in the app's user agent */
        versionCode: z.number().int(),
        versionName: z.string(),
        size: z.number().int(),
        /** of the APK file, hex */
        sha256: z.string(),
        /** what's new in this version (plain text, one item per line) */
        notes: z.string(),
        publishedAt: z.string(),
    }),
]);
/* ------------------------------------------------------------------- webhooks */
export const zWebhook = z.object({
    id: zSnowflake,
    guildId: zSnowflake,
    channelId: zSnowflake,
    name: z.string(),
    avatarUrl: z.string().nullable(),
    creatorId: zSnowflake.nullable(),
    createdAt: zIsoDate,
    /** only returned when created or when the token is rotated */
    token: z.string().optional(),
    /** path to POST messages to; only with `token` */
    url: z.string().optional(),
});
export const zWebhookName = z.string().trim().min(1).max(80);
export const zCreateWebhookBody = z.object({ name: zWebhookName });
export const zUpdateWebhookBody = z.object({
    name: zWebhookName.optional(),
    avatarAttachmentId: zSnowflake.nullable().optional(),
    channelId: zSnowflake.optional(),
});
export const zExecuteWebhookBody = z
    .object({
    content: z.string().trim().max(2000).default(''),
    /** shown instead of the webhook's name for this message */
    username: z.string().trim().min(1).max(80).optional(),
    embeds: zRichEmbedsInput.optional(),
    /** link buttons only: a webhook has no bot to receive clicks */
    components: zComponents.optional(),
})
    .refine((b) => b.content.length > 0 || !!b.embeds?.length, {
    message: 'a webhook message needs content or embeds',
});
export const zEditWebhookMessageBody = z
    .object({
    content: z.string().trim().max(2000).optional(),
    embeds: zRichEmbedsInput.optional(),
    components: zComponents.optional(),
})
    .refine((b) => b.content !== undefined || b.embeds !== undefined || b.components !== undefined, {
    message: 'nothing to change',
});
/* -------------------------------------------------------------------- automod */
export const MAX_AUTOMOD_RULES = 10;
export const zAutomodTrigger = z.discriminatedUnion('type', [
    z.object({
        type: z.literal('keyword'),
        /** case-insensitive; `word` matches the whole word, `*word*` anywhere, `word*` / `*word` as prefix/suffix */
        keywords: z.array(z.string().trim().min(1).max(60)).min(1).max(1000),
        /** words that never trigger the rule, same syntax */
        allowList: z.array(z.string().trim().min(1).max(60)).max(100).default([]),
    }),
    z.object({
        type: z.literal('links'),
        /** links to these domains (and their subdomains) are fine */
        allowedDomains: z
            .array(z
            .string()
            .trim()
            .toLowerCase()
            .regex(/^[a-z0-9.-]+\.[a-z]{2,}$/, 'a domain like example.com'))
            .max(100)
            .default([]),
    }),
    z.object({
        type: z.literal('mention_spam'),
        /** more distinct user/role mentions than this in one message trigger the rule */
        mentionLimit: z.number().int().min(1).max(50),
    }),
]);
export const zAutomodActions = z
    .object({
    /** stop the message; this text is shown to the sender */
    block: z.object({ message: z.string().max(150).nullable() }).nullable(),
    /** post the flagged message to this channel for moderators */
    alertChannelId: zSnowflake.nullable(),
    /** time the sender out (seconds) */
    timeoutSeconds: z.number().int().min(60).max(604800).nullable(),
})
    .refine((a) => a.block || a.alertChannelId || a.timeoutSeconds, {
    message: 'Pick at least one action',
});
export const zAutomodRule = z.object({
    id: zSnowflake,
    guildId: zSnowflake,
    name: z.string(),
    enabled: z.boolean(),
    trigger: zAutomodTrigger,
    actions: zAutomodActions,
    exemptRoles: z.array(zSnowflake),
    exemptChannels: z.array(zSnowflake),
    creatorId: zSnowflake.nullable(),
});
export const zCreateAutomodRuleBody = z.object({
    name: z.string().trim().min(1).max(100),
    enabled: z.boolean().default(true),
    trigger: zAutomodTrigger,
    actions: zAutomodActions,
    exemptRoles: z.array(zSnowflake).max(20).default([]),
    exemptChannels: z.array(zSnowflake).max(50).default([]),
});
export const zUpdateAutomodRuleBody = z.object({
    name: z.string().trim().min(1).max(100).optional(),
    enabled: z.boolean().optional(),
    trigger: zAutomodTrigger.optional(),
    actions: zAutomodActions.optional(),
    exemptRoles: z.array(zSnowflake).max(20).optional(),
    exemptChannels: z.array(zSnowflake).max(50).optional(),
});
export const zCreateEmojiBody = z.object({
    name: zEmojiName,
    /** an uploaded image (PNG, JPEG, WebP or GIF, max 512 KB); resized to 128px */
    attachmentId: zSnowflake,
});
export const zUpdateEmojiBody = z.object({ name: zEmojiName });
export const zCreateStickerBody = z.object({
    name: z.string().trim().min(2).max(30),
    description: z.string().max(100).default(''),
    /** an uploaded image (PNG, JPEG, WebP or GIF, max 512 KB); resized to 320px */
    attachmentId: zSnowflake,
});
export const zUpdateStickerBody = z.object({
    name: z.string().trim().min(2).max(30).optional(),
    description: z.string().max(100).optional(),
});
export const zUpdateNotificationBody = z.object({
    muted: z.boolean().optional(),
    level: zNotificationLevel.nullable().optional(),
});
export const zCreateInstanceInviteBody = z.object({
    maxUses: z.number().int().min(1).max(100).default(1),
    expiresInHours: z
        .number()
        .int()
        .min(1)
        .max(24 * 90)
        .optional(),
    note: z.string().max(200).optional(),
});
export const zInstanceInvite = z.object({
    code: z.string(),
    maxUses: z.number().int(),
    uses: z.number().int(),
    note: z.string().nullable(),
    expiresAt: zIsoDate.nullable(),
    createdAt: zIsoDate,
    createdBy: zSnowflake.nullable(),
});
export const zApiError = z.object({
    error: z.object({ code: z.string(), message: z.string(), details: z.unknown().optional() }),
});
/* -------------------------------------------------------------- admin health */
export const zHealthStatus = z.enum(['ok', 'warn', 'error']);
export const zAdminHealth = z.object({
    version: z.string(),
    uptimeSeconds: z.number(),
    /** worst status across checks */
    status: zHealthStatus,
    checks: z.array(z.object({ id: z.string(), label: z.string(), status: zHealthStatus, detail: z.string() })),
    counts: z.object({
        users: z.number(),
        guilds: z.number(),
        messages: z.number(),
        encryptedMessages: z.number(),
        attachments: z.number(),
        storageBytes: z.number(),
    }),
    /** last 7 days, oldest first */
    activity: z.array(z.object({ date: z.string(), messages: z.number(), signups: z.number() })),
    live: z.object({
        gatewayConnections: z.number(),
        voiceRooms: z.number(),
        voiceParticipants: z.number(),
    }),
    process: z.object({
        rssBytes: z.number(),
        heapUsedBytes: z.number(),
        /** CPU use of this process since the previous health check, 0–100 per core */
        cpuPercent: z.number(),
        eventLoopDelayMs: z.number(),
    }),
});
//# sourceMappingURL=schemas.js.map